import { Account, PayStatement, WorkLog } from "@wingspanhq/wingspan-api-sdk";

import { selectorPaystubDetails as selectorPayrollRunPaystubDetails } from "../../PayrollRuns/selectors/selectorPaystubDetails";
import { PaystubDetails } from "../types";

const EMPTY_VALUE = "—";

type Input = {
  payStatement: PayStatement;
  /** All work logs for the pay period; the base selector filters to this statement's sources. */
  workLogs: WorkLog[];
  /** The paying company's account (`payStatement.payerAccountId`) — supplies the employer name. */
  payerAccount?: Account;
};

/**
 * Adapt a v3 `PayStatement` (+ the period's work logs and the payer account)
 * into the payee's paystub detail view model.
 *
 * The income / deduction / work-log math is identical to the payer's payroll-runs
 * paystub view, so it is reused from `PayrollRuns/selectors/selectorPaystubDetails`
 * rather than reimplemented — that selector is the single source of truth for the
 * `PayStatement` → paystub transform. This adapter only reshapes it to the payee
 * view model: the payer's `createdBy` company is the payee's `employerName`, and
 * the employee name/address fields are dropped (the payee knows who they are).
 *
 * TODO(v3): the payout-method fields (`paymentMethodLabel`, `paymentSplit`,
 * `payoutMethodLabel`) fall back — resolving them needs PayoutSettings +
 * ExternalBankAccount, gated by a payer→payee bank-data grant (PLA-3198).
 */
export const selectorPaystubDetails = ({
  payStatement,
  workLogs,
  payerAccount
}: Input): PaystubDetails => {
  const base = selectorPayrollRunPaystubDetails({
    payStatement,
    workLogs,
    payerAccount
  });

  return {
    id: base.id,
    paystubNumber: base.paystubNumber,
    createdBy: base.createdBy,
    payPeriodRange: base.payPeriodRange,
    paidDate: base.paidDate,
    employerName: base.createdBy,
    paymentMethodLabel: EMPTY_VALUE,
    paymentSplit: 0,
    earnings: base.earnings,
    grossPayThisPeriod: base.grossPayThisPeriod,
    grossPayYearToDate: base.grossPayYearToDate,
    deductionGroups: base.deductionGroups,
    deductionTotalThisPeriod: base.deductionTotalThisPeriod,
    deductionTotalYearToDate: base.deductionTotalYearToDate,
    netPayThisPeriod: base.netPayThisPeriod,
    netPayYearToDate: base.netPayYearToDate,
    payoutMethodLabel: base.payoutMethodLabel,
    workLogs: base.workLogs,
    workLogsTotal: base.workLogsTotal
  };
};
